/**
 * copy_studio.jsx — Phase 3 of Rammy Studio.
 *
 * Multi-stage writing pipeline that sits BEFORE the draft:
 *   Brief → 3 Strategic Directions → Pick one → Send to Chat with the
 *   chosen direction as the anchor.
 *
 * Also exposes a stand-alone Fact-check panel — paste any draft, get a
 * per-claim classification (verified / inferred / unsupported) with an
 * overall safe / needs-review / unsafe verdict.
 *
 * Kept intentionally small: two workflows, no persistence, ships in one
 * screen. When the user picks a direction we route them straight to the
 * chat composer with a pre-filled prompt.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import * as L from "lucide-react";
import api from "@/lib/api";
import { detectContentType } from "@/lib/content-intent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CONTENT_TYPES = [
  { id: "linkedin", label: "LinkedIn post" },
  { id: "x_twitter", label: "Twitter/X thread" },
  { id: "instagram_caption", label: "Instagram caption" },
  { id: "blog_post", label: "Blog post" },
  { id: "email", label: "Email / newsletter" },
  { id: "ad_copy", label: "Ad copy" },
  { id: "press_release", label: "Press release" },
  { id: "thought_leadership", label: "Thought leadership" },
  { id: "personal_story", label: "Personal story" },
  { id: "readme", label: "README" },
  { id: "api_doc", label: "API doc" },
  { id: "privacy_policy", label: "Privacy policy" },
];

export function CopyStudioScreen({ go }) {
  const [tab, setTab] = useState("directions"); // "directions" | "factcheck"

  return (
    <div className="p-5 space-y-4 pb-20" data-testid="screen-copy-studio">
      <div className="flex items-center gap-3">
        <button
          onClick={() => go("settings")}
          className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)]"
          data-testid="copy-studio-back"
          aria-label="Back"
        >
          <L.ArrowLeft size={16}/>
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
            Rammy Studio · Phase 3
          </div>
          <h1 className="text-xl font-display text-[var(--rammy-text)]">Copy Studio</h1>
          <div className="text-[12px] text-[var(--rammy-text-sec)] mt-0.5">
            Explore strategic angles before you draft — and fact-check anything you&apos;ve already written.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 rounded-full bg-[var(--rammy-surface-alt)] p-1 w-fit">
        <TabBtn active={tab === "directions"} onClick={() => setTab("directions")} testid="tab-directions">
          <L.Compass size={13}/> 3 Directions
        </TabBtn>
        <TabBtn active={tab === "factcheck"} onClick={() => setTab("factcheck")} testid="tab-factcheck">
          <L.ShieldCheck size={13}/> Fact-check
        </TabBtn>
      </div>

      {tab === "directions" ? <DirectionsPanel go={go}/> : <FactcheckPanel/>}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium transition ${
        active ? "bg-[var(--rm-ink-900)] text-white shadow" : "text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)]"
      }`}
    >{children}</button>
  );
}

// ── Directions ────────────────────────────────────────────────────
function DirectionsPanel({ go }) {
  const [brief, setBrief] = useState("");
  const [contentType, setContentType] = useState("linkedin");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { directions, why_this_split }

  // Consume any brief the user stashed from Chat → Copy Studio via the
  // "Explore 3 directions first?" chip. Also try to auto-set the content
  // type by peeking at the brief through the same regex the chip uses.
  React.useEffect(() => {
    const stashed = typeof window !== "undefined" && window.__RAMMY_COPY_STUDIO_BRIEF__;
    if (stashed && typeof stashed === "string") {
      setBrief(stashed);
      const detected = detectContentType(stashed);
      if (detected) setContentType(detected);
      window.__RAMMY_COPY_STUDIO_BRIEF__ = null;
    }
  }, []);

  const run = async () => {
    const b = brief.trim();
    if (b.length < 10) return toast.error("Give me a bit more brief (≥10 chars)");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/content/directions", {
        brief: b, content_type: contentType,
      }, { timeout: 120000 });
      if (data.error) return toast.error("Directions temporarily unavailable — try again");
      if (!data.directions?.length) return toast.error("No directions returned — try a richer brief");
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't fetch directions");
    } finally {
      setLoading(false);
    }
  };

  const pick = (direction) => {
    // Compose a chat prompt that anchors the draft to this direction.
    const typeLabel = CONTENT_TYPES.find((t) => t.id === contentType)?.label || contentType;
    const prompt =
      `Write a ${typeLabel} using this strategic direction:\n\n` +
      `Angle: ${direction.angle}\n` +
      `Hook to open with: "${direction.hook_example}"\n` +
      `Tone: ${direction.tone}\n` +
      `Core argument to prove: ${direction.core_argument}\n\n` +
      `Original brief:\n${brief.trim()}`;
    try {
      // Stash the prefill on window so the chat screen can pick it up.
      window.__RAMMY_CHAT_PREFILL__ = prompt;
      go?.("chat");
      toast.success("Direction locked in — draft starting");
    } catch {
      toast.info("Copy the prompt manually into chat");
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Brief</div>
          <Textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="e.g. Announce we just shipped Rammy — a warm AI companion robot — to 100 early testers. Audience: Series-A founders. Vibe: quietly ambitious."
            className="mt-1.5 rounded-xl min-h-[100px]"
            data-testid="directions-brief"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Format</div>
          <Select value={contentType} onValueChange={setContentType}>
            <SelectTrigger className="h-8 rounded-full text-[12px] w-[220px]" data-testid="directions-format">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              {CONTENT_TYPES.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={run}
            disabled={loading || brief.trim().length < 10}
            className="ml-auto rounded-full h-8 px-4 bg-[var(--rm-ink-900)] hover:opacity-90 text-white text-[12px]"
            data-testid="directions-run"
          >
            {loading ? <L.Loader2 size={13} className="animate-spin"/> : <L.Sparkles size={13}/>}
            {loading ? "Thinking of angles…" : "Get 3 directions"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          {result.why_this_split && (
            <div className="text-[12px] text-[var(--rammy-text-sec)] leading-snug px-2">
              <span className="font-semibold">Why these three:</span> {result.why_this_split}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {result.directions.map((d) => (
              <DirectionCard key={d.id} direction={d} onPick={() => pick(d)}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DirectionCard({ direction, onPick }) {
  const rec = !!direction.recommended;
  return (
    <div
      className={`relative rounded-3xl p-4 border transition ${
        rec
          ? "bg-[var(--rm-surface)] border-[var(--rammy-cyan)]/60 shadow-[0_10px_30px_-16px_var(--rammy-cyan)]"
          : "bg-[var(--rm-surface)] border-[var(--rammy-border)]"
      }`}
      data-testid={`direction-card-${direction.id}`}
    >
      {rec && (
        <div className="absolute -top-2 left-4 h-5 px-2 rounded-full bg-gradient-to-r from-[var(--rammy-cyan)] to-emerald-400 text-[var(--rammy-text)] text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1">
          <L.Sparkles size={9}/> Recommended
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
        Direction {direction.id}
      </div>
      <div className="text-[15px] font-display font-semibold text-[var(--rammy-text)] mt-1">
        {direction.title}
      </div>
      <div className="text-[12px] text-[var(--rammy-text-sec)] mt-1.5 leading-snug">
        {direction.angle}
      </div>
      <div className="mt-3 rounded-xl bg-[var(--rammy-surface-alt)] p-2.5">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Hook</div>
        <div className="text-[12px] italic text-[var(--rammy-text)] mt-0.5">&ldquo;{direction.hook_example}&rdquo;</div>
      </div>
      <dl className="mt-3 space-y-1 text-[11px]">
        <MetaRow label="Tone" value={direction.tone}/>
        <MetaRow label="Feels" value={direction.target_emotion}/>
        <MetaRow label="Proves" value={direction.core_argument}/>
        <MetaRow label="Tradeoff" value={direction.tradeoff} muted/>
      </dl>
      <button
        onClick={onPick}
        data-testid={`direction-pick-${direction.id}`}
        className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-[12px] font-semibold transition ${
          rec
            ? "bg-[var(--rm-ink-900)] text-white hover:opacity-90"
            : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)] hover:bg-[var(--rammy-cyan)]/20"
        }`}
      >
        <L.ArrowRight size={12}/> Draft this direction
      </button>
    </div>
  );
}
function MetaRow({ label, value, muted }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="w-14 shrink-0 text-[var(--rammy-text-muted)] uppercase tracking-wider text-[10px] font-semibold pt-0.5">{label}</dt>
      <dd className={muted ? "text-[var(--rammy-text-muted)]" : "text-[var(--rammy-text)]"}>{value}</dd>
    </div>
  );
}

// ── Fact-check ────────────────────────────────────────────────────
function FactcheckPanel() {
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (draft.trim().length < 40) return toast.error("Paste at least ~40 chars of draft");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/content/factcheck", {
        draft: draft.trim(),
      }, { timeout: 120000 });
      if (data.error) return toast.error("Fact-check temporarily unavailable — try again");
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't fact-check");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Draft to check</div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Paste any draft here — claim by claim, Rammy will mark what's verified, inferred, or unsupported. Uses your Creative DNA's approved facts as evidence."
            className="mt-1.5 rounded-xl min-h-[140px]"
            data-testid="factcheck-draft"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={run}
            disabled={loading || draft.trim().length < 40}
            className="ml-auto rounded-full h-8 px-4 bg-[var(--rm-ink-900)] hover:opacity-90 text-white text-[12px]"
            data-testid="factcheck-run"
          >
            {loading ? <L.Loader2 size={13} className="animate-spin"/> : <L.ShieldCheck size={13}/>}
            {loading ? "Checking…" : "Fact-check"}
          </Button>
        </div>
      </div>

      {result && <FactcheckResult result={result}/>}
    </div>
  );
}

function FactcheckResult({ result }) {
  const s = result.summary || {};
  const overallColor = {
    safe:           "bg-emerald-100 text-emerald-900",
    "needs-review": "bg-amber-100 text-amber-900",
    unsafe:         "bg-rose-100 text-rose-900",
  }[s.overall] || "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)]";
  return (
    <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4" data-testid="factcheck-result">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 ${overallColor}`} data-testid="factcheck-overall">
          <L.ShieldCheck size={11}/> {s.overall || "—"}
        </span>
        <SummaryChip label="Verified" count={s.verified} tone="emerald"/>
        <SummaryChip label="Inferred" count={s.inferred} tone="amber"/>
        <SummaryChip label="Unsupported" count={s.unsupported} tone="rose"/>
      </div>
      <ul className="mt-3 space-y-2">
        {(result.claims || []).map((c, i) => (
          <li key={i} className="rounded-xl bg-[var(--rammy-surface-alt)]/60 p-3">
            <div className="flex items-start gap-2">
              <StatusBadge status={c.status}/>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] italic text-[var(--rammy-text)]">&ldquo;{c.quote}&rdquo;</div>
                <div className="text-[11px] text-[var(--rammy-text-sec)] mt-1">{c.reason}</div>
              </div>
            </div>
          </li>
        ))}
        {(!result.claims || !result.claims.length) && (
          <li className="text-[12px] text-[var(--rammy-text-muted)] italic">
            No substantive factual claims detected — this draft reads as opinion or narrative. Nothing to check.
          </li>
        )}
      </ul>
    </div>
  );
}
function SummaryChip({ label, count, tone }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-800",
    amber:   "bg-amber-50 text-amber-800",
    rose:    "bg-rose-50 text-rose-800",
  };
  return (
    <span className={`h-7 px-3 rounded-full text-[11px] font-medium inline-flex items-center gap-1 ${map[tone]}`}>
      {label} · {count ?? 0}
    </span>
  );
}
function StatusBadge({ status }) {
  const map = {
    verified:    { cls: "bg-emerald-100 text-emerald-800", icon: <L.Check size={10}/> },
    inferred:    { cls: "bg-amber-100 text-amber-800",     icon: <L.HelpCircle size={10}/> },
    unsupported: { cls: "bg-rose-100 text-rose-800",       icon: <L.AlertTriangle size={10}/> },
  };
  const cfg = map[status] || map.inferred;
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 h-5 px-2 rounded text-[9px] uppercase font-bold tracking-wider ${cfg.cls}`}>
      {cfg.icon} {status}
    </span>
  );
}
